import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { ChartConfiguration, ChartData, ChartType } from "chart.js";
import ChartDataLabels  from "chartjs-plugin-datalabels";
import { Dictionary } from "../../helper/util";
import { DateTime } from 'luxon';
import { TimeSeriesData } from "src/app/data/schema/reporting/time-series-data";
import { SelectOption } from "../form-inputs/select-input/select-input.component";
import { CurrencyHelper } from 'src/app/core/helper/currency.helper';

// Even further simplication of charting for single series, time spanning
// data. I.e. All you pass in is a title, axis label and a data series
// consising of a timestamp and value.
//
// this class will then summarize those by day and create a time series
// with full series of days to render on graph.
//
// finally, this component will deal with showing daily vs weekly values
// based on time span selected, which is also handled by this component
@Component({
  selector: 'app-time-series-chart',
  templateUrl: 'time-series-chart.html',
  styleUrls: ['time-series-chart.scss']
})
export class TimeSeriesChartComponent implements OnInit, OnChanges {

  // dictionary of x axis label to y axis value
  @Input() title: string;
  @Input() axisLegend: string;
  @Input() dataPoints: TimeSeriesData[] = [];
  @Input() propertyToChart: string = 'v1';

  // pass in array of select options to flip between which property
  // to use out of the TimeSeriesData. PropertyToChart above will be the default
  @Input() datasetPropertyOptions: SelectOption[];

  @Input() fromKey: number;
  @Input() toKey: number;

  @Input() horizontal: boolean = false;
  @Input() hideLegend: boolean = false;
  @Input() height: string = '300px';
  @Input() width: string;
  @Input() showFilter: boolean = true;
  @Input() revenueType: boolean = false;

  increment: string = 'daily';

  chartType: ChartType = 'bar';
  chartData: ChartData<'bar'>;
  chartOptions: ChartConfiguration['options'] = {
    responsive: true,
    // We use these empty structures as placeholders for dynamic theming.
    scales: {
      x: {},
      y: {
        min: 0,
      },
    },
    plugins: {
      legend: {
        display: true,
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
      },
    },
    // tension lets the line be curved a bit
    elements: {
      line: {
        tension: 0.5
      },
    },
  };

  loaded: boolean = false;

  // for the views by timespan (days, weeks) chart
  perTimespanDataDic: Dictionary<number> = {};

  constructor() {
  }

  ngOnChanges(changes: SimpleChanges) {
    // Log.debug('changes: ',  changes)
  }

  ngOnInit() {
    // Log.debug('time-series-chart loaded with data points: ', this.dataPoints)
    this.refresh();
    if (this.horizontal) {
      this.chartOptions.indexAxis = 'y'
    }
    // if ( this.hideLegend ) {
    this.chartOptions.plugins.legend.display = false
    // }

    if (this.revenueType) {
      this.chartOptions.plugins.tooltip = {
        callbacks: {
          label: function(context) {
            return CurrencyHelper.convertToDollarFromString(context.formattedValue);
          }
        }
      }
    }
  
    this.loaded = true;
  }

  // This should build a dataset based on the size of the range. I.e.
  // up to 60 days should be daily, 61 to 365 should be weekly, and greater should be monthly
  refresh() {
    //Log.debug('time series chart from ' +this.fromKey + ' to ' +this.toKey);

    if (this.fromKey > 0 == false || this.toKey > 0 == false) {
      // nothing to render yet, we need full from and to key
      return;
    }


    // if we have select options for different properties to graph, then update
    // the title to the currently selected option
    if (this.datasetPropertyOptions != null) {
      this.title = this.datasetPropertyOptions.find(m => { return m.value == this.propertyToChart }).displayValue;
    }

    // if from date is set to something in distant past, we are looking for
    // lifetime values, in which case we really want to look at the time from 
    // earliest value in data array 
    if (this.dataPoints.length > 0 && this.fromKey < 20200101) {
      this.fromKey = this.dataPoints[0].d
    } else if (this.fromKey < 20200101) {
      // if no data points but still all time, lets use a fromDate of one year ago
      const today = DateTime.local();
      let start = today.minus({ days: 365 });
      this.fromKey = +start.toFormat('yyyyMMdd');
    }

    let fromDate = this.convertToDateObject(this.fromKey);
    let toDate = this.convertToDateObject(this.toKey);

    var daysInRange = Math.ceil(Math.abs(
      (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)));

    // reset per timespan dictionary for new range calculation
    this.perTimespanDataDic = {};

    // Determine whether to switch to weekly aggregation
    if (daysInRange <= 69) {
      this.increment = 'daily';
      var currDateKey = this.fromKey;
      while (currDateKey <= this.toKey) {
        // init data dictionary for this day
        this.perTimespanDataDic[currDateKey] = 0;
        // add one day to the curr date. Need to convert to date object so we
        // capture month and year transitions
        const currDateObj = this.convertToDateObject(currDateKey);
        currDateObj.setDate(currDateObj.getDate() + 1);
        currDateKey = this.getDateKey(currDateObj);
      }

      // and now iterate data points and for each increment the count on the corresponding day
      this.dataPoints.forEach(r => {
        if (r.d >= this.fromKey && r.d <= this.toKey) {
          this.perTimespanDataDic[r.d] += r[this.propertyToChart];
        }
      });
    }
    else {
      this.increment = 'weekly';
      let currDateKey = this.fromKey;
      let year = 0;
      let weekNumber = 0;
      while (currDateKey <= this.toKey) {

        // Get the year and week number for the current date
        const currDateObj = this.convertToDateObject(currDateKey);

        year = currDateObj.getFullYear();
        weekNumber = this.getWeekNumber(currDateObj);

        // Calculate the week key (e.g., 202301 for the first week of 2023)
        const weekKey = `${year}${weekNumber.toString().padStart(2, '0')}`;

        // Initialize data dictionary for this week
        this.perTimespanDataDic[weekKey] = 0;

        // Move to the next week
        currDateObj.setDate(currDateObj.getDate() + 7);
        currDateKey = this.getDateKey(currDateObj);

      }

      // lets just add one more week since sometimes we miss the most recent data
      // there's probably a better way, but my brain hit a wall
      if (weekNumber == 52) {
        this.perTimespanDataDic[`${year + 1}01`] = 0;
      } else {
        this.perTimespanDataDic[`${year}${(weekNumber + 1).toString().padStart(2, '0')}`] = 0;
      }

      // Iterate data points and increment the count on the corresponding week
      this.dataPoints.forEach(r => {
        if (r.d >= this.fromKey && r.d <= this.toKey) {
          // Get the year and week number for the data point
          const dataDateObj = this.convertToDateObject(r.d);
          const dataYear = dataDateObj.getFullYear();
          const dataWeekNumber = this.getWeekNumber(dataDateObj);

          // Calculate the week key for the data point
          const dataWeekKey = `${dataYear}${dataWeekNumber.toString().padStart(2, '0')}`;

          this.perTimespanDataDic[dataWeekKey] += r[this.propertyToChart];
        }
      });
    }

    // convert date keys to readable date objects
    var xAxisLabels = Object.keys(this.perTimespanDataDic).map(this.formatDateString);

    this.chartData = {
      labels: xAxisLabels,
      datasets: [{
        data: Object.values(this.perTimespanDataDic),
        label: this.axisLegend
      }]
    };
  }

  // copied these next two functions to make this component more self sufficient
  private padValue(value: number) {
    return (value < 10) ? '0' + value : value;
  }

  private getDateKey(d: Date = new Date()) {
    return Number(d.getFullYear().toString() + this.padValue(d.getMonth() + 1).toString() + this.padValue(d.getDate()).toString());
  }

  private convertToDateObject(dateKey) {
    const dateKeyString = dateKey.toString();
    var year = dateKeyString.slice(0, 4);
    var month = dateKeyString.slice(4, 6);
    var day = dateKeyString.slice(6, 8);

    // Note: Months are zero-based in JavaScript Date objects
    return new Date(year, month - 1, day);
  }

  // Use luxon to get the week number of the year
  private getWeekNumber(d: Date) {
    const luxonDate = DateTime.fromJSDate(d);
    return luxonDate.weekNumber;
  }

  formatDateString(dateStr: string): string {
    if (dateStr.length === 8) { // YYYYMMDD format
      // Use Luxon's DateTime to parse and reformat
      return DateTime.fromFormat(dateStr, 'yyyyMMdd').toFormat('yyyy-MM-dd');
    } else if (dateStr.length === 6) { // YYYYWW format
      const year = parseInt(dateStr.substring(0, 4), 10);
      const week = parseInt(dateStr.substring(4), 10);

      // Get the DateTime for the first day of the year
      let dateTime = DateTime.fromObject({ year: year, month: 1, day: 1 });

      // Get to the first week
      dateTime = dateTime.plus({ weeks: week - 1 });

      // Now, we find the Monday of this week
      // ISO weeks start on Monday
      if (dateTime.weekday !== 1) {
        dateTime = dateTime.startOf('week').plus({ days: 1 });
      }

      // Return the formatted string
      return dateTime.toFormat('yyyy-MM-dd');
    } else {
      throw new Error(`Invalid date format: ${dateStr}`);
    }
  }
}
